import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Stethoscope } from 'lucide-react';
import type { ToothChartEntry } from '@his/shared';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Field, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';

export function ToothTreatmentPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const patientId = params.get('patientId') ?? '';
  const toothNo = params.get('toothNo') ?? '';
  const returnTo = safeReturnTo(params.get('returnTo'), `/dental?patientId=${patientId}`);
  const [finding, setFinding] = useState('');
  const [procedure, setProcedure] = useState('');
  const [chargeItemId, setChargeItemId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const chart = useQuery({
    queryKey: ['tooth-chart', patientId],
    queryFn: () => get<ToothChartEntry[]>(`/dental/patients/${patientId}/chart`),
    enabled: Boolean(patientId),
  });
  const existing = chart.data?.find((entry) => entry.toothNo === toothNo) ?? null;

  const charges = useQuery({
    queryKey: ['charge-items', 'dental'],
    queryFn: async () => (await get<{ data: { id: string; code: string; name: string }[] }>('/charge-items?limit=200')).data,
  });

  const plan = useMutation({
    mutationFn: () =>
      post('/dental/treatments', {
        patientId,
        toothNo,
        finding,
        procedure: procedure || undefined,
        chargeItemId: chargeItemId || null,
      }),
    onSuccess: () => {
      toast.success('Chart updated');
      void queryClient.invalidateQueries({ queryKey: ['tooth-chart', patientId] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  if (!patientId || !toothNo) return <RecordState missing />;

  const valid = finding.trim().length >= 2;
  const dirty = !plan.isSuccess && (finding !== '' || procedure !== '' || chargeItemId !== '');

  return (
    <FormScreen
      title={`Tooth ${toothNo}`}
      description={existing ? `${existing.finding} · ${humanize(existing.status)}` : 'Nothing charted yet'}
      icon={Stethoscope}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        setError(null);
        plan.mutate();
      }}
    >
      <FormSection title={tr('opd', 'details')} icon={Stethoscope} columns={1}>
        <Field label="Finding" required>
          <Input value={finding} onChange={(e) => setFinding(e.target.value)} placeholder="Deep caries" />
        </Field>
        <Field label="Planned procedure">
          <Input value={procedure} onChange={(e) => setProcedure(e.target.value)} placeholder="Composite filling" />
        </Field>
        <Field label="Charge">
          <Select value={chargeItemId} onChange={(e) => setChargeItemId(e.target.value)}>
            <option value="">Not priced yet</option>
            {charges.data?.map((charge) => (
              <option key={charge.id} value={charge.id}>
                {charge.name}
              </option>
            ))}
          </Select>
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Add to plan" loading={plan.isPending} incomplete={!valid} cancelTo={returnTo} />
    </FormScreen>
  );
}
